import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import dayjs from 'dayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import Navbar from '../components/Navbar';
import { trackToolAction } from '../utils/metrics';
import { generateRiderDocx } from '../utils/generatePdf';
import {
  TIERS, TIER_ORDER, TIER_EXPLAIN, classifyIdentifier, identifierLine, recordsFor, allowedBy, numberRecords,
} from '../data/legalProcess';
import {
  PROVIDERS, PROVIDER_BY_ID, CATEGORIES, REVIEWED, SEARCH_ISP_LIST, servedBy, searchProviders, suggestFor,
} from '../data/providers';

const STORAGE_KEY = 'riderUserInfo';
const PURPLE = '#6d28d9';

const BY_LABEL = {
  url: 'profile URL', id: 'account ID', email: 'email address', phone: 'phone number', handle: 'username',
  ip: 'IP address with date, time & time zone', imei: 'IMEI', imsi: 'IMSI', iccid: 'ICCID', crypto: 'wallet address',
  cashtag: '$cashtag', vin: 'VIN', acct: 'account number', card: 'debit or credit card number',
  name_dob: 'name and date of birth', ssn: 'Social Security number',
};

const NOTIFY = {
  yes: { text: 'Notifies the user unless you include a non-disclosure order (18 U.S.C. § 2705(b)).', color: '#b45309', bg: '#fffbeb', border: '#fde68a' },
  no: { text: 'Generally does not notify the user.', color: '#15803d', bg: '#f0fdf4', border: '#bbf7d0' },
  varies: { text: 'Notice policy varies or is unpublished. Include a non-disclosure order if notice would hurt the case.', color: '#475569', bg: '#f8fafc', border: '#e2e8f0' },
};

const PRESETS = [
  ['30 days', d => d.subtract(30, 'day')],
  ['90 days', d => d.subtract(90, 'day')],
  ['6 months', d => d.subtract(6, 'month')],
  ['1 year', d => d.subtract(1, 'year')],
  ['2 years', d => d.subtract(2, 'year')],
];

const inputSx = { '& .MuiOutlinedInput-root': { borderRadius: '8px' } };
const cardSx = { borderRadius: '14px', boxShadow: '0 1px 3px rgba(0,0,0,.07), 0 4px 16px rgba(0,0,0,.05)', p: { xs: '20px 16px', sm: '26px 26px 22px' }, mb: 2.5 };
const labelSx = { fontSize: '13px', color: '#374151', mb: 1, fontWeight: 600 };
const linkSx = { color: PURPLE, fontSize: '13.5px', fontWeight: 600, textDecoration: 'none', '&:hover': { textDecoration: 'underline' } };

// ─── Small pieces ───────────────────────────────────────────────────────────

function Pill({ children, color = '#475569', bg = '#f1f5f9', border, title }) {
  return (
    <Box component="span" title={title} sx={{ display: 'inline-flex', alignItems: 'center', gap: '4px', fontSize: '11.5px', fontWeight: 700, color, bgcolor: bg, border: `1px solid ${border || bg}`, px: '8px', py: '2px', borderRadius: '99px', whiteSpace: 'nowrap' }}>
      {children}
    </Box>
  );
}

const TierPill = ({ tier }) => <Pill color={TIERS[tier].color} bg={TIERS[tier].bg}>{TIERS[tier].short}</Pill>;

function ExtLink({ href, children }) {
  return <Box component="a" href={href} target="_blank" rel="noopener noreferrer" sx={linkSx}>{children}</Box>;
}

function Section({ title, children, sx }) {
  return (
    <Box sx={{ mt: 2.5, ...sx }}>
      <Typography sx={{ fontSize: '12px', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '.06em', mb: 1 }}>{title}</Typography>
      {children}
    </Box>
  );
}

function ProviderRow({ p, selected, onSelect }) {
  const served = servedBy(p);
  return (
    <Box component="button" type="button" onClick={() => onSelect(p.id)}
      sx={{
        width: '100%', textAlign: 'left', display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap',
        p: '10px 12px', border: '1px solid', borderColor: selected ? '#c4b5fd' : '#e2e8f0', borderRadius: '10px',
        bgcolor: selected ? '#f5f3ff' : '#fff', cursor: 'pointer', fontFamily: 'inherit', '&:hover': { bgcolor: '#f5f3ff' },
      }}>
      <Box sx={{ flex: '1 1 220px', minWidth: 0 }}>
        <Typography sx={{ fontSize: '14px', fontWeight: 700, color: selected ? '#5b21b6' : '#1e293b' }}>{p.name}</Typography>
        <Typography sx={{ fontSize: '12px', color: '#64748b' }}>
          {p.serveAs ? `Serve ${served.name}` : p.company}
        </Typography>
      </Box>
      <Box sx={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
        {(served.guide || served.portal) && <Pill color="#0369a1" bg="#e0f2fe">LE guide</Pill>}
        {served.emergency?.h24 && <Pill color="#b91c1c" bg="#fee2e2">24/7 emergency</Pill>}
      </Box>
    </Box>
  );
}

// ─── Provider details ───────────────────────────────────────────────────────

function ProviderDetail({ p, onSelect, preserveItem }) {
  const served = servedBy(p);
  const records = recordsFor(served);
  const em = served.emergency;
  const notify = NOTIFY[served.notify] || NOTIFY.varies;

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, flexWrap: 'wrap' }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontSize: '19px', fontWeight: 800, color: '#0f172a' }}>{p.name}</Typography>
          <Typography sx={{ fontSize: '13px', color: '#64748b' }}>{p.company}</Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
          {CATEGORIES.filter(c => c.id === p.cat).map(c => <Pill key={c.id}>{c.label}</Pill>)}
        </Box>
      </Box>

      {p.serveAs && (
        <Box sx={{ mt: 2, p: '12px 14px', bgcolor: '#f5f3ff', border: '1px solid #ddd6fe', borderRadius: '10px' }}>
          <Typography sx={{ fontSize: '13.5px', color: '#4c1d95' }}>
            Legal process for {p.name} goes to <b>{served.name}</b>. Everything below is {served.name}&rsquo;s.
          </Typography>
          {(p.notes || []).map((n, i) => <Typography key={i} sx={{ fontSize: '13px', color: '#5b21b6', mt: '4px' }}>{n}</Typography>)}
          <Button size="small" onClick={() => onSelect(served.id)} sx={{ mt: 1, textTransform: 'none', fontWeight: 700, color: PURPLE }}>Open {served.name} &rarr;</Button>
        </Box>
      )}

      <Section title="How to serve">
        {served.submit && <Typography sx={{ fontSize: '14px', color: '#334155', lineHeight: 1.55 }}>{served.submit}</Typography>}
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mt: 1 }}>
          {served.portal && <ExtLink href={served.portal}>Law enforcement portal &#8599;</ExtLink>}
          {served.guide && served.guide !== served.portal && <ExtLink href={served.guide}>Provider&rsquo;s law enforcement guide &#8599;</ExtLink>}
          {!served.guide && !served.portal && (
            <Typography sx={{ fontSize: '13px', color: '#64748b' }}>
              No public law enforcement guide found. <ExtLink href={SEARCH_ISP_LIST}>Check the SEARCH ISP List &#8599;</ExtLink>
            </Typography>
          )}
        </Box>
        {served.address && (
          <Box sx={{ mt: 1.5, p: '10px 12px', bgcolor: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '13px', color: '#334155', lineHeight: 1.5 }}>
            {served.address.map((l, i) => <div key={i}>{l}</div>)}
          </Box>
        )}
      </Section>

      {em && (
        <Box sx={{ mt: 2.5, p: '14px 16px', bgcolor: '#fef2f2', border: '1px solid #fecaca', borderRadius: '10px' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: '6px' }}>
            <Typography sx={{ fontSize: '13px', fontWeight: 800, color: '#991b1b', textTransform: 'uppercase', letterSpacing: '.05em' }}>Emergency / exigent request</Typography>
            {em.h24 ? <Pill color="#fff" bg="#dc2626">24/7</Pill> : <Pill color="#991b1b" bg="#fee2e2">Not 24/7</Pill>}
          </Box>
          <Typography sx={{ fontSize: '14px', color: '#7f1d1d', lineHeight: 1.55 }}>{em.how}</Typography>
          {em.phone && (
            <Typography sx={{ fontSize: '14px', color: '#7f1d1d', mt: '6px' }}>
              Phone: <Box component="a" href={`tel:${em.phone.replace(/[^\d]/g, '').slice(0, 10)}`} sx={{ color: '#b91c1c', fontWeight: 700 }}>{em.phone}</Box>
            </Typography>
          )}
          {em.url && <Box sx={{ mt: '6px' }}><ExtLink href={em.url}>Emergency form &#8599;</ExtLink></Box>}
          {em.note && <Typography sx={{ fontSize: '13px', color: '#991b1b', mt: 1, lineHeight: 1.5 }}>{em.note}</Typography>}
          <Typography sx={{ fontSize: '12px', color: '#b91c1c', mt: 1 }}>
            Providers may disclose without legal process when they believe in good faith that an emergency involving danger of death or serious physical injury requires it (18 U.S.C. § 2702(b)(8) and (c)(4)). Confirm phone numbers before relying on them.
          </Typography>
        </Box>
      )}

      <Box sx={{ mt: 2, p: '10px 14px', bgcolor: notify.bg, border: `1px solid ${notify.border}`, borderRadius: '8px' }}>
        <Typography sx={{ fontSize: '13px', color: notify.color, fontWeight: 600 }}>{notify.text}</Typography>
      </Box>

      <Section title="Preservation">
        <Typography sx={{ fontSize: '13.5px', color: '#334155', lineHeight: 1.55 }}>
          {served.preserve || 'Send a preservation request under 18 U.S.C. § 2703(f) as early as you can. Providers hold the snapshot for 90 days and extend once for another 90 on request.'}
        </Typography>
        {served.preserveKey && (
          <Box component={Link} to={`/preservation?carrier=${served.preserveKey}${preserveItem ? `&item=${encodeURIComponent(preserveItem)}` : ''}`} sx={{ ...linkSx, display: 'inline-block', mt: 1 }}>
            Create a preservation letter for {served.name} &rarr;
          </Box>
        )}
      </Section>

      <Section title="What they hold, and the process each needs">
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' }, gap: 1.5 }}>
          {TIER_ORDER.map(t => {
            const items = records.filter(r => r.tier === t);
            return (
              <Box key={t} sx={{ p: '10px 12px', border: '1px solid #e2e8f0', borderRadius: '10px', bgcolor: '#fff' }}>
                <Box sx={{ mb: 1 }}><TierPill tier={t} /></Box>
                {items.length === 0
                  ? <Typography sx={{ fontSize: '12.5px', color: '#94a3b8' }}>Nothing listed</Typography>
                  : items.map(r => <Typography key={r.id} sx={{ fontSize: '12.5px', color: '#334155', lineHeight: 1.5 }}>&bull; {r.label}</Typography>)}
              </Box>
            );
          })}
        </Box>
      </Section>

      {!p.serveAs && (served.notes || []).length > 0 && (
        <Section title="Know before you serve">
          {served.notes.map((n, i) => (
            <Typography key={i} sx={{ fontSize: '13.5px', color: '#334155', lineHeight: 1.55, mb: '6px' }}>&bull; {n}</Typography>
          ))}
        </Section>
      )}

      {(served.by || []).length > 0 && (
        <Section title="They can find the account by">
          <Box sx={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
            {served.by.map(b => <Pill key={b}>{BY_LABEL[b] || b}</Pill>)}
          </Box>
        </Section>
      )}

      {(served.tools || []).length > 0 && (
        <Section title="When the return comes back">
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            {served.tools.map(t => <Box key={t.to} component={Link} to={t.to} sx={linkSx}>{t.label} &rarr;</Box>)}
          </Box>
        </Section>
      )}
    </Box>
  );
}

// ─── "What do you have?" result ─────────────────────────────────────────────

function IdentifierResult({ c, onSelect, onAdd, added, servedName }) {
  const s = suggestFor(c);
  if (!s) return null;
  return (
    <Box sx={{ mt: 2, p: '14px 16px', bgcolor: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '10px' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
        <Pill color={PURPLE} bg="#f5f3ff" border="#ddd6fe">{c.label}</Pill>
        <Typography sx={{ fontSize: '13.5px', color: '#334155', fontWeight: 600, wordBreak: 'break-all' }}>{c.value}</Typography>
        <Button size="small" onClick={() => onAdd(s.matches.length === 1 ? s.matches[0] : null)} disabled={added}
          sx={{ ml: 'auto', textTransform: 'none', fontWeight: 700, color: PURPLE }}>
          {added ? 'Added \u2713' : 'Add to rider'}
        </Button>
      </Box>
      {added && (
        <Typography sx={{ fontSize: '12.5px', color: '#15803d', mt: '6px' }}>
          {servedName ? `On the ${servedName} rider below.` : 'Saved for the rider. Choose who to serve and it will be filled in.'}
        </Typography>
      )}

      {s.matches.length > 0 && (
        <Box sx={{ mt: 1.5 }}>
          <Typography sx={labelSx}>Serve</Typography>
          {s.matches.map(p => <ProviderRow key={p.id} p={p} onSelect={onSelect} />)}
        </Box>
      )}

      {s.note && <Typography sx={{ fontSize: '13.5px', color: '#334155', mt: 1.5, lineHeight: 1.55 }}>{s.note}</Typography>}

      {s.links.length > 0 && (
        <Box sx={{ mt: 1, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          {s.links.map(l => <Box key={l.to} component={Link} to={l.to} sx={linkSx}>{l.label} &rarr;</Box>)}
        </Box>
      )}

      {s.also.length > 0 && (
        <Box sx={{ mt: 1.5 }}>
          <Typography sx={labelSx}>{c.type === 'phone' ? 'Apps and services that can search by phone number' : 'Providers to consider'}</Typography>
          {CATEGORIES.map(cat => ({ cat, ps: s.also.filter(p => p.cat === cat.id) })).filter(g => g.ps.length).map(({ cat, ps }, _, groups) => (
            <Box key={cat.id} sx={{ display: 'flex', gap: '6px', flexWrap: 'wrap', alignItems: 'center', mb: '6px' }}>
              {groups.length > 1 && <Typography sx={{ fontSize: '12px', fontWeight: 700, color: '#94a3b8', minWidth: 92 }}>{cat.label}</Typography>}
              {ps.map(p => (
                <Box key={p.id} component="button" type="button" onClick={() => onSelect(p.id)}
                  sx={{ fontFamily: 'inherit', fontSize: '12.5px', fontWeight: 600, color: '#334155', bgcolor: '#fff', border: '1px solid #e2e8f0', borderRadius: '99px', px: '10px', py: '4px', cursor: 'pointer', '&:hover': { borderColor: '#c4b5fd', color: PURPLE } }}>
                  {p.name}
                </Box>
              ))}
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
}

// ─── Page ───────────────────────────────────────────────────────────────────

export default function SubpoenaRider() {
  const [params] = useSearchParams();
  // Two ways in: search the directory by name, or paste an identifier and let the page work out who holds it.
  const [mode, setMode] = useState(params.get('q') ? 'identify' : 'search');
  const [search, setSearch] = useState('');
  const [query, setQuery] = useState(params.get('q') || '');
  const [cat, setCat] = useState('');
  const [providerId, setProviderId] = useState(PROVIDER_BY_ID[params.get('provider')] ? params.get('provider') : '');
  const detailRef = useRef(null);

  const [caseNumber, setCaseNumber] = useState('');
  const [name, setName] = useState('');
  const [title, setTitle] = useState('');
  const [email, setEmail] = useState('');
  const [telephone, setTelephone] = useState('');
  const [saveInfo, setSaveInfo] = useState(false);

  const [legalProcess, setLegalProcess] = useState('subpoena');
  const [checked, setChecked] = useState(() => new Set());
  const [address, setAddress] = useState('');
  const [identifierInput, setIdentifierInput] = useState('');
  const [identifiers, setIdentifiers] = useState([]);
  const [startDate, setStartDate] = useState(null);
  const [toPresent, setToPresent] = useState(true);
  const [endDate, setEndDate] = useState(null);
  const [dueDate, setDueDate] = useState(dayjs().add(2, 'week'));
  const [error, setError] = useState('');
  const [generated, setGenerated] = useState(false);

  // Load saved personal info on mount.
  useEffect(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        const o = JSON.parse(saved);
        setName(o.name || ''); setTitle(o.title || '');
        setEmail(o.email || ''); setTelephone(o.telephone || '');
        setSaveInfo(true);
      }
    } catch { /* ignore */ }
  }, []);

  // Persist (or clear) saved info as it changes.
  useEffect(() => {
    try {
      if (saveInfo) localStorage.setItem(STORAGE_KEY, JSON.stringify({ name, title, email, telephone }));
      else localStorage.removeItem(STORAGE_KEY);
    } catch { /* ignore */ }
  }, [saveInfo, name, title, email, telephone]);

  const selected = PROVIDER_BY_ID[providerId];
  const served = servedBy(selected);
  const records = useMemo(() => recordsFor(served), [served]);

  // New provider or process: reset the checklist to what that process can reach,
  // and the "To:" block to the provider's address.
  useEffect(() => {
    setChecked(new Set(records.filter(r => allowedBy(r.tier, legalProcess)).map(r => r.id)));
  }, [records, legalProcess]);
  useEffect(() => {
    setAddress(served ? (served.address || [served.company]).join('\n') : '');
  }, [served]);

  // Directory search: any word of a name, brand, domain or category that starts with the query
  // (or, from three letters, a name or brand that contains it), with names that start with it first.
  const list = useMemo(() => {
    const q = search.trim().toLowerCase();
    const catLabel = id => CATEGORIES.find(c => c.id === id)?.label || '';
    const hit = p => {
      if (!q) return true;
      const names = [p.name, p.company, ...(p.aka || [])].filter(Boolean).map(v => v.toLowerCase());
      const words = [...names, ...(p.domains || []), catLabel(p.cat).toLowerCase()].join(' ').split(/[^a-z0-9&$]+/);
      return words.some(w => w.startsWith(q)) || names.some(n => n.startsWith(q) || (q.length >= 3 && n.includes(q)));
    };
    const starts = p => (q && p.name.toLowerCase().startsWith(q) ? 0 : 1);
    return PROVIDERS.filter(p => hit(p) && (!cat || p.cat === cat)).sort((a, b) => starts(a) - starts(b) || a.name.localeCompare(b.name));
  }, [search, cat]);

  // "What do you have?": a single word could be a company name or a username, so a directory match wins.
  const nameMatches = useMemo(() => searchProviders(query), [query]);
  const classified = useMemo(() => {
    const c = classifyIdentifier(query);
    if (!c || c.type === 'text' || (c.type === 'handle' && nameMatches.length)) return null;
    return c;
  }, [query, nameMatches]);

  function selectProvider(id) {
    setProviderId(id);
    setError(''); setGenerated(false);
    setTimeout(() => detailRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 50);
  }

  const timePeriodText = useMemo(() => {
    if (!startDate) return '';
    const start = dayjs(startDate).format('MMMM D, YYYY');
    if (toPresent) return `${start} to present`;
    if (!endDate) return '';
    return `${start} to ${dayjs(endDate).format('MMMM D, YYYY')}`;
  }, [startDate, toPresent, endDate]);

  const dueDateText = useMemo(() => (dueDate ? dayjs(dueDate).format('MMMM D, YYYY') : ''), [dueDate]);

  function addIdentifier(raw) {
    const v = String(raw ?? identifierInput).trim();
    if (!v) return;
    setIdentifiers(prev => (prev.includes(identifierLine(v)) ? prev : [...prev, identifierLine(v)]));
    if (raw === undefined) setIdentifierInput('');
  }

  // "Add to rider" from the lookup: if the identifier points at one provider, open that provider's rider too.
  const lookupValue = classified?.type === 'phone' ? classified.value : classified?.type === 'url' && classified.handle ? classified.handle : query;
  function addFromLookup(match) {
    addIdentifier(lookupValue);
    if (match && servedBy(match)?.id !== served?.id) selectProvider(match.id);
  }

  // Identifiers that look like they belong to a different provider than this rider's.
  const mismatches = useMemo(() => identifiers.map(id => {
    const c = classifyIdentifier(id.replace(/^[^:]+:\s*/, ''));
    const s = c && (c.type === 'email' || c.type === 'url' || c.type === 'cashtag') ? suggestFor(c) : null;
    const other = s?.matches[0] && servedBy(s.matches[0]);
    return other && served && other.id !== served.id ? `${id} looks like a ${other.name} account, but this rider is for ${served.name}.` : null;
  }).filter(Boolean), [identifiers, served]);

  const chosen = records.filter(r => checked.has(r.id));
  const overreach = chosen.filter(r => !allowedBy(r.tier, legalProcess));

  function toggle(id) {
    setChecked(prev => { const n = new Set(prev); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  }

  function generate() {
    trackToolAction('/subpoenarider', 'Subpoena Rider Generator');
    setError('');
    setGenerated(false);
    if (!served) { setError('Select a provider.'); return; }
    if (chosen.length === 0) { setError('Choose at least one type of record.'); return; }
    if (identifiers.length === 0) { setError('Add at least one account identifier.'); return; }
    if (!startDate) { setError('Choose a start date for the records.'); return; }
    if (!toPresent && !endDate) { setError('Choose an end date, or select "Through present".'); return; }
    if (!dueDate) { setError('Choose a due date.'); return; }

    generateRiderDocx({
      providerKey: served.id,
      providerName: served.company || served.name,
      address: address.split('\n').map(l => l.trim()).filter(Boolean),
      records: numberRecords(chosen),
      docTitle: TIERS[legalProcess].doc,
      process: legalProcess,
      caseNumber, identifiers, timePeriodText, dueDateText,
      name, title, email, telephone,
      currentDate: dayjs().format('MM-DD-YY'),
    }).catch(() => setError('Failed to generate the document.'));
    setGenerated(true);
  }

  function clearSavedInfo() {
    setSaveInfo(false);
    try { localStorage.removeItem(STORAGE_KEY); } catch { /* ignore */ }
    setName(''); setTitle(''); setEmail(''); setTelephone('');
  }

  const firstPhone = identifiers.map(i => i.match(/^Phone number:\s*(.+)$/)).find(Boolean)?.[1];

  return (
    <>
      <Navbar pageTitle="Provider Directory & Rider Generator" />
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Box sx={{ mb: 3 }}>
          <Typography variant="h5" sx={{ fontWeight: 800, color: '#0f172a', letterSpacing: '-0.4px' }}>
            Provider Directory &amp; Rider Generator
          </Typography>
          <Typography sx={{ color: '#64748b', mt: '6px', fontSize: '15px' }}>
            Paste what you have to find who to serve, how they take legal process and emergency requests, and what
            records each kind of process can reach. Then build the subpoena rider or warrant attachment as a Word document.
          </Typography>
          <Typography sx={{ color: '#94a3b8', mt: 1, fontSize: '12.5px', lineHeight: 1.5 }}>
            Compiled by LE Tech Tools from providers&rsquo; public law enforcement guidance, reviewed {REVIEWED}. Policies
            and contacts change; confirm with the provider&rsquo;s current guide before serving. Process levels follow
            federal law (the Stored Communications Act); some states require more, e.g. California&rsquo;s CalECPA
            requires a warrant for most electronic records. Nothing you type here leaves your browser.
          </Typography>
        </Box>

        {/* Find the provider */}
        <Paper elevation={0} sx={cardSx}>
          <Box role="tablist" sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '4px', p: '4px', bgcolor: '#f1f5f9', borderRadius: '10px', mb: 2 }}>
            {[{ id: 'search', label: 'Find a provider' }, { id: 'identify', label: 'What do you have?' }].map(t => (
              <Box key={t.id} component="button" type="button" role="tab" aria-selected={mode === t.id} onClick={() => setMode(t.id)}
                sx={{ fontFamily: 'inherit', fontSize: '13.5px', fontWeight: 700, py: '8px', border: 0, borderRadius: '7px', cursor: 'pointer',
                  bgcolor: mode === t.id ? '#fff' : 'transparent', color: mode === t.id ? PURPLE : '#64748b',
                  boxShadow: mode === t.id ? '0 1px 2px rgba(0,0,0,.08)' : 'none' }}>
                {t.label}
              </Box>
            ))}
          </Box>

          {mode === 'search' ? (
            <>
              <TextField
                fullWidth size="small" value={search} onChange={e => setSearch(e.target.value)} sx={inputSx}
                placeholder="Provider, brand or category (Verizon, Cricket, Tinder, bank…)"
                inputProps={{ 'aria-label': 'Search providers' }}
              />

              <Box sx={{ display: 'flex', gap: '6px', flexWrap: 'wrap', mt: 2 }}>
                {[{ id: '', label: 'All' }, ...CATEGORIES].map(c => (
                  <Box key={c.id} component="button" type="button" onClick={() => setCat(c.id)}
                    sx={{ fontFamily: 'inherit', fontSize: '12.5px', fontWeight: 600, px: '10px', py: '4px', borderRadius: '99px', cursor: 'pointer',
                      border: '1px solid', borderColor: cat === c.id ? PURPLE : '#e2e8f0', bgcolor: cat === c.id ? PURPLE : '#fff', color: cat === c.id ? '#fff' : '#475569' }}>
                    {c.label}
                  </Box>
                ))}
              </Box>

              <Box sx={{ mt: 1.5, display: 'flex', flexDirection: 'column', gap: 1, maxHeight: 380, overflowY: 'auto', pr: '2px' }}>
                {list.length === 0 && (
                  <Typography sx={{ fontSize: '13px', color: '#64748b', py: 1 }}>
                    Not in this directory. <ExtLink href={SEARCH_ISP_LIST}>Search the SEARCH ISP List &#8599;</ExtLink>
                  </Typography>
                )}
                {list.map(p => <ProviderRow key={p.id} p={p} selected={p.id === providerId} onSelect={selectProvider} />)}
              </Box>
              <Typography sx={{ fontSize: '12px', color: '#94a3b8', mt: 1 }}>
                {list.length} of {PROVIDERS.length} providers. For anyone not listed, see the{' '}
                <ExtLink href={SEARCH_ISP_LIST}>SEARCH ISP List</ExtLink>.
              </Typography>
            </>
          ) : (
            <>
              <TextField
                fullWidth size="small" value={query} onChange={e => setQuery(e.target.value)} sx={inputSx} autoFocus
                placeholder="Email, phone, profile URL, username, $cashtag, IP, IMEI, wallet address or VIN"
                inputProps={{ 'aria-label': 'Identifier' }}
              />
              <Typography sx={{ fontSize: '12.5px', color: '#94a3b8', mt: 1 }}>
                Paste an identifier from your case and this works out who holds the records.
              </Typography>

              {nameMatches.length > 0 && !classified && (
                <Box sx={{ mt: 2 }}>
                  <Typography sx={labelSx}>Providers matching &ldquo;{query.trim()}&rdquo;</Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {nameMatches.map(p => <ProviderRow key={p.id} p={p} selected={p.id === providerId} onSelect={selectProvider} />)}
                  </Box>
                </Box>
              )}
              {classified && (
                <IdentifierResult c={classified} onSelect={selectProvider} onAdd={addFromLookup}
                  added={identifiers.includes(identifierLine(lookupValue))} servedName={served?.name} />
              )}
              {query.trim().length >= 2 && !classified && nameMatches.length === 0 && (
                <Typography sx={{ fontSize: '13px', color: '#64748b', mt: 2 }}>
                  Not a recognized identifier or provider. <ExtLink href={SEARCH_ISP_LIST}>Search the SEARCH ISP List &#8599;</ExtLink>
                </Typography>
              )}
            </>
          )}
        </Paper>

        {/* Provider details */}
        <Box ref={detailRef} sx={{ scrollMarginTop: '16px' }}>
          {selected && (
            <Paper elevation={0} sx={cardSx}>
              <ProviderDetail p={selected} onSelect={selectProvider} preserveItem={firstPhone} />
            </Paper>
          )}
        </Box>

        {/* Rider builder */}
        {served && (
          <Paper elevation={0} sx={cardSx}>
            <Typography sx={{ fontSize: '14px', fontWeight: 700, color: '#0f172a', mb: 2 }}>Build the {served.name} rider</Typography>

            <Typography sx={labelSx}>Legal process</Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' }, gap: 1 }}>
              {TIER_ORDER.map(t => (
                <Box key={t} component="button" type="button" onClick={() => setLegalProcess(t)}
                  sx={{ fontFamily: 'inherit', textAlign: 'left', p: '10px 12px', borderRadius: '10px', cursor: 'pointer',
                    border: '1.5px solid', borderColor: legalProcess === t ? PURPLE : '#e2e8f0', bgcolor: legalProcess === t ? '#f5f3ff' : '#fff' }}>
                  <Typography sx={{ fontSize: '14px', fontWeight: 700, color: legalProcess === t ? '#5b21b6' : '#1e293b' }}>{TIERS[t].label}</Typography>
                  <Typography sx={{ fontSize: '11.5px', color: '#64748b' }}>Produces {TIERS[t].doc === 'RIDER' ? 'a rider' : 'an Attachment B'}</Typography>
                </Box>
              ))}
            </Box>
            <Typography sx={{ fontSize: '12.5px', color: '#64748b', mt: 1, lineHeight: 1.5 }}>{TIER_EXPLAIN[legalProcess]}</Typography>

            <Typography sx={{ ...labelSx, mt: 2.5 }}>Records to request</Typography>
            <Typography sx={{ fontSize: '12.5px', color: '#64748b', mb: 1 }}>
              Pre-checked: everything a {TIERS[legalProcess].label.toLowerCase()} can reach at {served.name}. Uncheck what you don&rsquo;t need; narrow requests come back faster.
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
              {records.map(r => (
                <Box key={r.id} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, p: '4px 6px', borderRadius: '8px', opacity: allowedBy(r.tier, legalProcess) || checked.has(r.id) ? 1 : 0.6, '&:hover': { bgcolor: '#f8fafc' } }}>
                  <Checkbox size="small" checked={checked.has(r.id)} onChange={() => toggle(r.id)} sx={{ p: '3px', color: PURPLE, '&.Mui-checked': { color: PURPLE } }} />
                  <Box sx={{ flex: 1, minWidth: 0, pt: '2px' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                      <Typography sx={{ fontSize: '13.5px', fontWeight: 600, color: '#1e293b' }}>{r.label}</Typography>
                      <TierPill tier={r.tier} />
                    </Box>
                    <Typography sx={{ fontSize: '12px', color: '#64748b', lineHeight: 1.45 }}>{r.text}</Typography>
                  </Box>
                </Box>
              ))}
            </Box>
            {overreach.length > 0 && (
              <Box sx={{ mt: 1.5, p: '10px 14px', bgcolor: '#fffbeb', border: '1px solid #fde68a', borderRadius: '8px' }}>
                <Typography sx={{ fontSize: '13px', color: '#92400e', lineHeight: 1.5 }}>
                  {overreach.map(r => r.label).join(', ')} generally {overreach.length > 1 ? 'need' : 'needs'} more than a {TIERS[legalProcess].label.toLowerCase()}. The provider will likely refuse {overreach.length > 1 ? 'those items' : 'that item'}.
                </Typography>
              </Box>
            )}

            <Typography sx={{ ...labelSx, mt: 2.5 }}>Account identifiers</Typography>
            {(served.by || []).length > 0 && (
              <Typography sx={{ fontSize: '12.5px', color: '#64748b', mb: 1 }}>
                {served.name} can find the account by {served.by.map(b => BY_LABEL[b] || b).join(', ')}.
              </Typography>
            )}
            <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
              <TextField
                placeholder="e.g. phone number, email, username, account ID"
                variant="outlined" fullWidth size="small"
                value={identifierInput}
                onChange={e => setIdentifierInput(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addIdentifier(); } }}
                sx={inputSx}
              />
              <Button onClick={() => addIdentifier()} variant="contained"
                sx={{ flexShrink: 0, p: '8px 18px', bgcolor: PURPLE, fontSize: '13.5px', fontWeight: 600, borderRadius: '8px', whiteSpace: 'nowrap', '&:hover': { bgcolor: '#5b21b6' } }}>
                Add
              </Button>
            </Box>
            {identifiers.length > 0 && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 1.5 }}>
                {identifiers.map((id, idx) => (
                  <Box key={id} sx={{ display: 'flex', alignItems: 'center', gap: 1, p: '8px 12px', bgcolor: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '8px' }}>
                    <Typography sx={{ fontSize: '13px', color: '#374151', flex: 1, wordBreak: 'break-word' }}>{id}</Typography>
                    <Box component="button" type="button" aria-label={`Remove ${id}`} onClick={() => setIdentifiers(prev => prev.filter((_, i) => i !== idx))}
                      sx={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8', fontSize: '18px', lineHeight: 1, p: '2px 6px', '&:hover': { color: '#dc2626' } }}>
                      &times;
                    </Box>
                  </Box>
                ))}
              </Box>
            )}
            {mismatches.map(m => (
              <Typography key={m} sx={{ fontSize: '12.5px', color: '#b45309', mt: 1 }}>{m}</Typography>
            ))}

            <Typography sx={{ ...labelSx, mt: 2.5 }}>Time period for records</Typography>
            <Box sx={{ display: 'flex', gap: '6px', flexWrap: 'wrap', mb: 1.5 }}>
              {PRESETS.map(([label, fn]) => (
                <Box key={label} component="button" type="button" onClick={() => { setStartDate(fn(dayjs()).startOf('day')); setToPresent(true); }}
                  sx={{ fontFamily: 'inherit', fontSize: '12.5px', fontWeight: 600, color: '#475569', bgcolor: '#fff', border: '1px solid #e2e8f0', borderRadius: '99px', px: '10px', py: '4px', cursor: 'pointer', '&:hover': { borderColor: '#c4b5fd', color: PURPLE } }}>
                  Last {label}
                </Box>
              ))}
            </Box>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
                <DatePicker label="Start date" value={startDate} onChange={setStartDate} slotProps={{ textField: { sx: inputSx } }} />
                {!toPresent && <DatePicker label="End date" value={endDate} onChange={setEndDate} slotProps={{ textField: { sx: inputSx } }} />}
              </Box>
            </LocalizationProvider>
            <FormControlLabel
              sx={{ mt: 1 }}
              control={<Checkbox checked={toPresent} onChange={e => setToPresent(e.target.checked)} sx={{ color: PURPLE, '&.Mui-checked': { color: PURPLE } }} />}
              label={<Typography sx={{ fontSize: '13.5px', color: '#374151' }}>Through present</Typography>}
            />

            <Typography sx={{ ...labelSx, mt: 2 }}>Due date</Typography>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker label="Due date" value={dueDate} onChange={setDueDate} slotProps={{ textField: { sx: inputSx } }} />
            </LocalizationProvider>
            <Typography sx={{ fontSize: '12px', color: '#94a3b8', mt: '6px' }}>Defaults to two weeks from today.</Typography>

            <Typography sx={{ ...labelSx, mt: 2.5 }}>&ldquo;To:&rdquo; block</Typography>
            <TextField fullWidth multiline minRows={3} value={address} onChange={e => setAddress(e.target.value)} sx={inputSx}
              helperText={served.address ? 'From the directory; edit if the provider has moved.' : 'No mailing address on file for this provider. Add it from their guide or SEARCH, or leave the company name if you are submitting through a portal.'} />
          </Paper>
        )}

        {/* Your information */}
        {served && (
          <Paper elevation={0} sx={cardSx}>
            <Typography sx={{ fontSize: '14px', fontWeight: 700, color: '#0f172a', mb: 1 }}>Your Information</Typography>
            <TextField label="Case Number" variant="outlined" fullWidth margin="normal" value={caseNumber} onChange={e => setCaseNumber(e.target.value)} sx={inputSx} />
            <TextField label="Name" variant="outlined" fullWidth margin="normal" value={name} onChange={e => setName(e.target.value)} sx={inputSx} />
            <TextField label="Title" variant="outlined" fullWidth margin="normal" value={title} onChange={e => setTitle(e.target.value)} sx={inputSx} />
            <TextField label="Email" variant="outlined" fullWidth margin="normal" value={email} onChange={e => setEmail(e.target.value)} sx={inputSx} />
            <TextField label="Telephone Number" variant="outlined" fullWidth margin="normal" value={telephone} onChange={e => setTelephone(e.target.value)} sx={inputSx} />

            <Box sx={{ mt: 1, p: '12px 14px', bgcolor: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '8px' }}>
              <FormControlLabel
                control={<Checkbox checked={saveInfo} onChange={e => setSaveInfo(e.target.checked)} sx={{ color: PURPLE, '&.Mui-checked': { color: PURPLE } }} />}
                label={<Typography sx={{ fontSize: '13.5px', color: '#374151', fontWeight: 600 }}>Save my information on this browser for next time</Typography>}
              />
              <Typography sx={{ fontSize: '12px', color: '#b45309', mt: '2px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
                Do not enable this on a shared computer — your name, title, email, and phone are stored locally in this browser. (Case number is never saved.)
              </Typography>
              {saveInfo && (
                <Button onClick={clearSavedInfo} size="small" variant="outlined"
                  sx={{ mt: 1.5, borderColor: '#fca5a5', color: '#dc2626', fontWeight: 700, textTransform: 'none', borderRadius: '8px', '&:hover': { borderColor: '#dc2626', bgcolor: '#fef2f2' } }}>
                  Clear saved information
                </Button>
              )}
            </Box>

            {error && <Box sx={{ bgcolor: '#fef2f2', color: '#dc2626', p: '10px 14px', borderRadius: '8px', mt: 2, fontSize: '14px' }}>{error}</Box>}

            <Button fullWidth variant="contained" onClick={generate}
              startIcon={
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="12" y1="18" x2="12" y2="12"/><line x1="9" y1="15" x2="15" y2="15"/></svg>
              }
              sx={{ mt: 2.5, p: '12px 20px', bgcolor: '#4c1d95', fontSize: '15px', fontWeight: 600, borderRadius: '10px', '&:hover': { bgcolor: '#3b1378' } }}>
              Generate {legalProcess === 'subpoena' ? 'Rider' : 'Attachment'} (Word)
            </Button>

            {generated && (
              <Box sx={{ mt: 2, p: '12px 14px', bgcolor: '#fffbeb', border: '1px solid #fde68a', borderRadius: '8px' }}>
                <Typography sx={{ fontSize: '12.5px', color: '#92400e', lineHeight: 1.5 }}>
                  Review the document with your prosecutor or legal advisor before serving, and check {served.name}&rsquo;s current guide for service details.
                </Typography>
              </Box>
            )}
          </Paper>
        )}
      </Container>
    </>
  );
}
